// ===============================================
// API Configuration for PHP Backend
// ===============================================

// PHP Backend Base URLs
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost/Candidate_google_reviews_BE_php/api/v1';
const HEALTH_CHECK_URL = process.env.REACT_APP_HEALTH_URL || 'http://localhost/Candidate_google_reviews_BE_php/health';

// ===============================================
// PARAMETER CLEANING UTILITY
// ===============================================

/**
 * Clean parameters by removing empty strings, null, undefined values
 * This prevents sending empty parameters that cause database issues
 */
const cleanParams = (params) => {
  if (!params || typeof params !== 'object') return {};
  
  const cleaned = {};
  
  for (const [key, value] of Object.entries(params)) {
    // Skip empty strings, null, undefined, and whitespace-only strings
    if (value !== null && 
        value !== undefined && 
        value !== '' && 
        (typeof value !== 'string' || value.trim() !== '')) {
      
      // Handle boolean conversion for string booleans
      if (typeof value === 'string') {
        if (value.toLowerCase() === 'true') {
          cleaned[key] = true;
        } else if (value.toLowerCase() === 'false') {
          cleaned[key] = false;
        } else {
          cleaned[key] = value;
        }
      } else {
        cleaned[key] = value;
      }
    }
  }
  
  return cleaned;
};

/**
 * Build query string from clean parameters
 * Only includes non-empty values
 */
const buildQueryString = (params) => {
  const cleanedParams = cleanParams(params);
  
  if (Object.keys(cleanedParams).length === 0) {
    return '';
  }
  
  const searchParams = new URLSearchParams();
  
  for (const [key, value] of Object.entries(cleanedParams)) {
    searchParams.append(key, String(value));
  }
  
  return searchParams.toString();
};

// ===============================================
// AUTH HEADER HELPERS
// ===============================================

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

const getPublicHeaders = () => {
  return {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  };
};

// ===============================================
// GENERIC API REQUEST FUNCTIONS
// ===============================================

/**
 * Generic API request with proper CORS handling for PHP backend
 */
const apiRequest = async (endpoint, options = {}, useAuth = true) => {
  const url = `${API_BASE_URL}${endpoint}`;
  const config = {
    headers: useAuth ? getAuthHeaders() : getPublicHeaders(),
    credentials: 'include',
    mode: 'cors',
    ...options
  };

  try {
    console.log(`[API Request] ${config.method || 'GET'} ${url}`);
    
    const response = await fetch(url, config);
    
    // Check if response is JSON
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      const data = await response.json();
      
      if (!response.ok) {
        console.error(`[API Error] ${response.status}:`, data);
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }
      
      console.log(`[API Success] ${url}`, data.success ? '✓' : '✗');
      return data;
    } else {
      // Handle non-JSON responses (like file downloads)
      if (!response.ok) {
        const text = await response.text();
        console.error(`[API Error] ${response.status}:`, text);
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response;
    }
  } catch (error) {
    console.error('[API Request Failed]:', {
      url,
      error: error.message,
      endpoint
    });
    throw error;
  }
};

// ===============================================
// AUTHENTICATION APIs
// ===============================================

export const login = async (credentials) => {
  const response = await apiRequest('/auth/login', {
    method: 'POST',
    body: JSON.stringify(credentials)
  }, false);
  
  // Store user data and token after successful login
  if (response.success && response.data) {
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
    }
    if (response.data.user) {
      localStorage.setItem('user', JSON.stringify(response.data.user));
      console.log('Login successful - stored user:', response.data.user);
    }
  }
  
  return response;
};

export const register = async (userData) => {
  const response = await apiRequest('/auth/register', {
    method: 'POST',
    body: JSON.stringify(userData)
  }, false);
  
  // Store user data and token after successful registration
  if (response.success && response.data) {
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
    }
    if (response.data.user) {
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
  }
  
  return response;
};

// ===============================================
// USER MANAGEMENT UTILITIES
// ===============================================

export const getCurrentUser = () => {
  try {
    const userStr = localStorage.getItem('user');
    if (!userStr) return {};
    
    const user = JSON.parse(userStr);
    console.log('getCurrentUser - Retrieved user:', user);
    return user;
  } catch (error) {
    console.error('Error getting current user:', error);
    return {};
  }
};

export const isAuthenticated = () => {
  const token = localStorage.getItem('token');
  if (!token) {
    console.log('No token found');
    return false;
  }
  
  try {
    const tokenPayload = JSON.parse(atob(token.split('.')[1]));
    const isNotExpired = tokenPayload.exp * 1000 > Date.now();
    console.log('Token check:', { isNotExpired });
    return isNotExpired;
  } catch (error) {
    console.error('Token validation error:', error);
    return false;
  }
};

export const checkAdminRole = () => {
  const user = getCurrentUser();
  
  if (!user || Object.keys(user).length === 0) {
    console.log('checkAdminRole - No user data found');
    return false;
  }
  
  // Check multiple possible ways the role might be stored
  const isAdmin = user.role === 'admin' || 
                  user.role === 'Admin' || 
                  user.user_role === 'admin' || 
                  user.user_role === 'Admin' ||
                  user.role?.toLowerCase() === 'admin';
  
  console.log('checkAdminRole - User:', user, 'isAdmin:', isAdmin);
  return isAdmin;
};

export const isAdminAuthenticated = () => {
  const isAuth = isAuthenticated();
  const isAdmin = checkAdminRole();
  
  console.log('isAdminAuthenticated - Auth:', isAuth, 'Admin:', isAdmin);
  return isAuth && isAdmin;
};

export const logout = () => {
  console.log('Logging out user...');
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  window.location.href = '/login';
};

// ===============================================
// USER PROFILE APIs
// ===============================================

export const getUserProfile = async () => {
  return apiRequest('/user/profile');
};

export const updateUserProfile = async (profileData) => {
  return apiRequest('/user/profile', {
    method: 'PUT',
    body: JSON.stringify(profileData)
  });
};

// ===============================================
// DASHBOARD APIs
// ===============================================

export const getDashboardData = async () => {
  return apiRequest('/dashboard');
};

export const getAnalytics = async (params = {}) => {
  const queryString = buildQueryString(params);
  const endpoint = queryString ? `/dashboard/analytics?${queryString}` : '/dashboard/analytics';
  return apiRequest(endpoint);
};

// ===============================================
// ADMIN DASHBOARD APIs
// ===============================================

export const getAdminOverviewAnalytics = async (params = {}) => {
  const queryString = buildQueryString(params);
  const endpoint = queryString ? `/admin/dashboard/overview?${queryString}` : '/admin/dashboard/overview';
  return apiRequest(endpoint);
};

export const getAdminBUAnalysis = async (params = {}) => {
  const queryString = buildQueryString(params);
  const endpoint = queryString ? `/admin/dashboard/bu-analysis?${queryString}` : '/admin/dashboard/bu-analysis';
  return apiRequest(endpoint);
};

export const getAdminBURecruiters = async (buId, params = {}) => {
  const queryString = buildQueryString(params);
  const endpoint = queryString ? `/admin/dashboard/bu/${buId}/recruiters?${queryString}` : `/admin/dashboard/bu/${buId}/recruiters`;
  return apiRequest(endpoint);
};

export const getAdminRecruiterAnalysis = async (params = {}) => {
  const queryString = buildQueryString(params);
  const endpoint = queryString ? `/admin/dashboard/recruiter-analysis?${queryString}` : '/admin/dashboard/recruiter-analysis';
  return apiRequest(endpoint);
};

export const getAdminTopPerformers = async (params = {}) => {
  const queryString = buildQueryString(params);
  const endpoint = queryString ? `/admin/dashboard/top-performers?${queryString}` : '/admin/dashboard/top-performers';
  return apiRequest(endpoint);
};

export const getAdminCommentsAnalytics = async (params = {}) => {
  const queryString = buildQueryString(params);
  const endpoint = queryString ? `/admin/dashboard/comments-analytics?${queryString}` : '/admin/dashboard/comments-analytics';
  return apiRequest(endpoint);
};

export const getAdminDetailedComments = async (params = {}) => {
  const queryString = buildQueryString(params);
  const endpoint = queryString ? `/admin/dashboard/comments/detailed?${queryString}` : '/admin/dashboard/comments/detailed';
  return apiRequest(endpoint);
};

export const getAdminBusinessUnitsFilter = async () => {
  return apiRequest('/admin/dashboard/filters/business-units');
};

export const getAdminRecruitersFilter = async (params = {}) => {
  const queryString = buildQueryString(params);
  const endpoint = queryString ? `/admin/dashboard/filters/recruiters?${queryString}` : '/admin/dashboard/filters/recruiters';
  return apiRequest(endpoint);
};

export const getAdminCommentFilterOptions = async () => {
  return apiRequest('/admin/dashboard/filters/comment-options');
};

export const exportAdminDashboardData = async (params = {}) => {
  const queryString = buildQueryString(params);
  const endpoint = queryString ? `/admin/dashboard/export?${queryString}` : '/admin/dashboard/export';
  return apiRequest(endpoint);
};

// ===============================================
// ADMIN USER MANAGEMENT APIs
// ===============================================

export const getAllUsersAdmin = async (params = {}) => {
  const queryString = buildQueryString(params);
  const endpoint = queryString ? `/admin/users?${queryString}` : '/admin/users';
  
  console.log('getAllUsersAdmin - Final endpoint:', endpoint);
  return apiRequest(endpoint);
};

export const getUserDetailsAdmin = async (userId) => {
  return apiRequest(`/admin/users/${userId}`);
};

export const getUserActivityAdmin = async (userId, params = {}) => {
  const queryString = buildQueryString(params);
  const endpoint = queryString ? `/admin/users/${userId}/activity?${queryString}` : `/admin/users/${userId}/activity`;
  return apiRequest(endpoint);
};

export const getUserPerformanceAdmin = async (userId, params = {}) => {
  const queryString = buildQueryString(params);
  const endpoint = queryString ? `/admin/users/${userId}/performance?${queryString}` : `/admin/users/${userId}/performance`;
  return apiRequest(endpoint);
};

export const updateUserAdmin = async (userId, userData) => {
  return apiRequest(`/admin/users/${userId}`, {
    method: 'PUT',
    body: JSON.stringify(userData)
  });
};

export const deactivateUserAdmin = async (userId, reason) => {
  return apiRequest(`/admin/users/${userId}/deactivate`, {
    method: 'POST',
    body: JSON.stringify({ reason })
  });
};

export const resetUserPasswordAdmin = async (userId, tempPassword = null) => {
  const body = tempPassword ? { temporary_password: tempPassword } : {};
  return apiRequest(`/admin/users/${userId}/reset-password`, {
    method: 'POST',
    body: JSON.stringify(body)
  });
};

export const promoteUserToAdmin = async (userId, reason) => {
  return apiRequest(`/admin/users/${userId}/promote`, {
    method: 'POST',
    body: JSON.stringify({ reason })
  });
};

// ===============================================
// ADMIN TICKET MANAGEMENT APIs
// ===============================================

export const getAllTicketsAdmin = async (params = {}) => {
  const queryString = buildQueryString(params);
  const endpoint = queryString ? `/admin/tickets?${queryString}` : '/admin/tickets';
  
  console.log('getAllTicketsAdmin - Final endpoint:', endpoint);
  return apiRequest(endpoint);
};

export const getTicketDetailsAdmin = async (ticketId) => {
  return apiRequest(`/admin/tickets/${ticketId}`);
};

export const getTicketHistoryAdmin = async (ticketId) => {
  return apiRequest(`/admin/tickets/${ticketId}/history`);
};

export const getOverdueTicketsAdmin = async (daysOverdue = 0) => {
  const endpoint = daysOverdue ? `/admin/tickets/overdue?days_overdue=${daysOverdue}` : '/admin/tickets/overdue';
  return apiRequest(endpoint);
};

export const getTicketStatisticsAdmin = async (months = 6) => {
  return apiRequest(`/admin/tickets/stats?months=${months}`);
};

export const updateTicketStatusAdmin = async (ticketId, statusData) => {
  return apiRequest(`/admin/tickets/${ticketId}/update-status`, {
    method: 'POST',
    body: JSON.stringify(statusData)
  });
};

export const bulkUpdateTicketStatusAdmin = async (ticketIds, status, notes) => {
  return apiRequest('/admin/tickets/bulk/update-status', {
    method: 'POST',
    body: JSON.stringify({ 
      ticket_ids: ticketIds, 
      ticket_status: status, 
      admin_notes: notes 
    })
  });
};

export const bulkSendRemindersAdmin = async (ticketIds) => {
  return apiRequest('/admin/tickets/bulk/send-reminders', {
    method: 'POST',
    body: JSON.stringify({ ticket_ids: ticketIds })
  });
};

export const bulkExpireTicketsAdmin = async (daysOverdue = 30) => {
  return apiRequest('/admin/tickets/bulk/expire-tickets', {
    method: 'POST',
    body: JSON.stringify({ days_overdue: daysOverdue })
  });
};

// ===============================================
// ADMIN REVIEW MANAGEMENT APIs
// ===============================================

export const getAllReviewsAdmin = async (params = {}) => {
  const queryString = buildQueryString(params);
  const endpoint = queryString ? `/admin/reviews?${queryString}` : '/admin/reviews';
  
  console.log('getAllReviewsAdmin - Final endpoint:', endpoint);
  return apiRequest(endpoint);
};

export const getReviewDetailsAdmin = async (reviewId) => {
  return apiRequest(`/admin/reviews/${reviewId}`);
};

export const getReviewStatisticsAdmin = async (months = 12) => {
  return apiRequest(`/admin/reviews/stats?months=${months}`);
};

export const getReviewsByRecruiterAdmin = async (recruiterId, params = {}) => {
  const queryString = buildQueryString(params);
  const endpoint = queryString ? `/admin/reviews/by-recruiter/${recruiterId}?${queryString}` : `/admin/reviews/by-recruiter/${recruiterId}`;
  return apiRequest(endpoint);
};

export const getReviewTrendsAdmin = async (params = {}) => {
  const queryString = buildQueryString(params);
  const endpoint = queryString ? `/admin/reviews/trends?${queryString}` : '/admin/reviews/trends';
  return apiRequest(endpoint);
};

// ===============================================
// ADMIN EXPORT APIs
// ===============================================

export const exportAllRecruitersDataAdmin = async (params = {}) => {
  const queryString = buildQueryString(params);
  const endpoint = queryString ? `/admin/export/all-recruiters?${queryString}` : '/admin/export/all-recruiters';
  return apiRequest(endpoint);
};

export const exportSingleRecruiterDataAdmin = async (recruiterId, params = {}) => {
  const queryString = buildQueryString(params);
  const endpoint = queryString ? `/admin/export/recruiter/${recruiterId}?${queryString}` : `/admin/export/recruiter/${recruiterId}`;
  return apiRequest(endpoint);
};

export const exportReviewsDataAdmin = async (params = {}) => {
  const queryString = buildQueryString(params);
  const endpoint = queryString ? `/admin/export/reviews?${queryString}` : '/admin/export/reviews';
  return apiRequest(endpoint);
};

export const exportTicketsDataAdmin = async (params = {}) => {
  const queryString = buildQueryString(params);
  const endpoint = queryString ? `/admin/export/tickets?${queryString}` : '/admin/export/tickets';
  return apiRequest(endpoint);
};

export const exportUserActivityAdmin = async (userId, params = {}) => {
  const queryString = buildQueryString(params);
  const endpoint = queryString ? `/admin/export/user-activity/${userId}?${queryString}` : `/admin/export/user-activity/${userId}`;
  return apiRequest(endpoint);
};

// ===============================================
// ADMIN SYSTEM MANAGEMENT APIs
// ===============================================

export const getSystemStatisticsAdmin = async () => {
  return apiRequest('/admin/system/stats');
};

export const runSystemCleanupAdmin = async () => {
  return apiRequest('/admin/system/cleanup', { method: 'POST' });
};

export const getMaintenanceStatusAdmin = async () => {
  return apiRequest('/admin/system/maintenance');
};

// ===============================================
// ADMIN REPORTING APIs
// ===============================================

export const getPerformanceSummaryReportAdmin = async (months = 3) => {
  return apiRequest(`/admin/reports/performance-summary?months=${months}`);
};

export const getMonthlySummaryReportAdmin = async (year, month) => {
  return apiRequest(`/admin/reports/monthly-summary?year=${year}&month=${month}`);
};

export const generateCustomReportAdmin = async (reportType, filters) => {
  return apiRequest('/admin/reports/custom', {
    method: 'POST',
    body: JSON.stringify({ report_type: reportType, filters })
  });
};

// ===============================================
// ADMIN COMMENT MANAGEMENT APIs
// ===============================================

export const getAllCandidateCommentsAdmin = async (params = {}) => {
  const queryString = buildQueryString(params);
  const endpoint = queryString ? `/admin/comments?${queryString}` : '/admin/comments';
  return apiRequest(endpoint);
};

export const deleteCandidateCommentAdmin = async (commentId, reason) => {
  return apiRequest(`/admin/comments/${commentId}`, {
    method: 'DELETE',
    body: JSON.stringify({ reason })
  });
};

// ===============================================
// ADMIN LOOKUP DATA APIs
// ===============================================

export const getAdminDropdownData = async () => {
  return apiRequest('/admin/lookups/dropdown-data');
};

// ===============================================
// PUBLIC LOOKUP APIs (No Auth)
// ===============================================

export const getDropdownData = async () => {
  return apiRequest('/public/dropdown-data', {}, false);
};

export const getFilteredTeams = async (buId, bfId) => {
  const params = cleanParams({ bu_id: buId, bf_id: bfId });
  const queryString = Object.keys(params).length > 0 ? `?${buildQueryString(params)}` : '';
  const endpoint = `/public/teams${queryString}`;
  return apiRequest(endpoint, {}, false);
};

export const getFilteredClients = async (geoId) => {
  const params = cleanParams({ geo_id: geoId });
  const queryString = Object.keys(params).length > 0 ? `?${buildQueryString(params)}` : '';
  const endpoint = `/public/clients${queryString}`;
  return apiRequest(endpoint, {}, false);
};

// ===============================================
// PROTECTED LOOKUP APIs
// ===============================================

export const getDropdownDataProtected = async () => {
  return apiRequest('/lookups/dropdown-data');
};

export const getFilteredTeamsProtected = async (buId, bfId) => {
  const params = cleanParams({ bu_id: buId, bf_id: bfId });
  const queryString = Object.keys(params).length > 0 ? `?${buildQueryString(params)}` : '';
  const endpoint = `/lookups/teams${queryString}`;
  return apiRequest(endpoint);
};

export const getFilteredClientsProtected = async (geoId) => {
  const params = cleanParams({ geo_id: geoId });
  const queryString = Object.keys(params).length > 0 ? `?${buildQueryString(params)}` : '';
  const endpoint = `/lookups/clients${queryString}`;
  return apiRequest(endpoint);
};

// ===============================================
// CANDIDATE APIs
// ===============================================

export const getCandidates = async (page = 1, limit = 20) => {
  return apiRequest(`/candidates?page=${page}&limit=${limit}`);
};

export const getCandidateDetails = async (candidateId) => {
  return apiRequest(`/candidates/details?candidate_id=${candidateId}`);
};

export const getUpcomingCandidates = async (days = 7) => {
  return apiRequest(`/candidates/upcoming?days=${days}`);
};

export const searchCandidates = async (searchParams) => {
  const params = buildQueryString(searchParams);
  return apiRequest(`/candidates/search?${params}`);
};

// ===============================================
// CANDIDATE COMMENTS APIs
// ===============================================

export const addCandidateComment = async (commentData) => {
  return apiRequest('/candidates/comments', {
    method: 'POST',
    body: JSON.stringify(commentData)
  });
};

export const getCandidateComments = async (candidateId) => {
  return apiRequest(`/candidates/${candidateId}/comments`);
};

export const updateCandidateComment = async (commentId, updateData) => {
  return apiRequest(`/candidates/comments/${commentId}`, {
    method: 'PUT',
    body: JSON.stringify(updateData)
  });
};

export const deleteCandidateComment = async (commentId) => {
  return apiRequest(`/candidates/comments/${commentId}`, {
    method: 'DELETE'
  });
};

// ===============================================
// REVIEW APIs
// ===============================================

export const raiseReviewRequest = async (reviewData) => {
  return apiRequest('/reviews/create', {
    method: 'POST',
    body: JSON.stringify(reviewData)
  });
};

export const getReviewTickets = async (status = null, limit = 50) => {
  const params = cleanParams({ status, limit });
  const queryString = buildQueryString(params);
  const endpoint = queryString ? `/reviews/tickets?${queryString}` : '/reviews/tickets';
  return apiRequest(endpoint);
};

export const updateTicket = async (ticketData) => {
  return apiRequest('/reviews/update', {
    method: 'PUT',
    body: JSON.stringify(ticketData)
  });
};

export const updateReviewStatus = async (statusData) => {
  return apiRequest('/reviews/update-status', {
    method: 'POST',
    body: JSON.stringify(statusData)
  });
};

export const submitReview = async (reviewData) => {
  return apiRequest('/reviews/submit', {
    method: 'POST',
    body: JSON.stringify(reviewData)
  });
};

export const sendReminder = async (ticketId) => {
  return apiRequest('/reviews/reminder', {
    method: 'POST',
    body: JSON.stringify({ ticket_id: ticketId })
  });
};

// ===============================================
// TEMPLATE APIs
// ===============================================

export const getEmailTemplates = async (geoId, bfId, templateType) => {
  const params = cleanParams({ geo_id: geoId, bf_id: bfId, template_type: templateType });
  const queryString = buildQueryString(params);
  const endpoint = queryString ? `/templates?${queryString}` : '/templates';
  
  return apiRequest(endpoint);
};

export const createTemplate = async (templateData) => {
  return apiRequest('/templates', {
    method: 'POST',
    body: JSON.stringify(templateData)
  });
};

// ===============================================
// SIGNATURE APIs
// ===============================================

export const getUserSignatures = async () => {
  return apiRequest('/signatures');
};

export const getSignatureById = async (signatureId) => {
  return apiRequest(`/signatures/${signatureId}`);
};

export const createSignature = async (signatureData) => {
  return apiRequest('/signatures', {
    method: 'POST',
    body: JSON.stringify(signatureData)
  });
};

export const updateSignature = async (signatureId, signatureData) => {
  return apiRequest(`/signatures/${signatureId}`, {
    method: 'PUT',
    body: JSON.stringify(signatureData)
  });
};

export const deleteSignature = async (signatureId) => {
  return apiRequest(`/signatures/${signatureId}`, {
    method: 'DELETE'
  });
};

export const setDefaultSignature = async (signatureId) => {
  return apiRequest(`/signatures/${signatureId}/set-default`, {
    method: 'POST'
  });
};

export const getDefaultSignature = async () => {
  return apiRequest('/signatures/default');
};

export const previewSignature = async (signatureHtml) => {
  return apiRequest('/signatures/preview', {
    method: 'POST',
    body: JSON.stringify({ signature_html: signatureHtml })
  });
};

// ===============================================
// FILE UPLOAD API
// ===============================================

export const uploadFile = async (file) => {
  const token = localStorage.getItem('token');
  const formData = new FormData();
  formData.append('file', file);

  try {
    const response = await fetch(`${API_BASE_URL}/upload`, {
      method: 'POST',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` })
      },
      credentials: 'include',
      mode: 'cors',
      body: formData
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || `HTTP error! status: ${response.status}`);
    }
    
    return data;
  } catch (error) {
    console.error('File upload failed:', error);
    throw error;
  }
};

export const serveFile = async (filename) => {
  return apiRequest(`/uploads/${filename}`);
};

// ===============================================
// SYSTEM/UTILITY APIs
// ===============================================

export const validateToken = async () => {
  return apiRequest('/utils/validate-token');
};

export const getSystemInfo = async () => {
  return apiRequest('/utils/system-info');
};

export const healthCheck = async () => {
  try {
    const response = await fetch(HEALTH_CHECK_URL, {
      mode: 'cors',
      credentials: 'include'
    });
    return await response.json();
  } catch (error) {
    console.error('Health check failed:', error);
    throw error;
  }
};

// ===============================================
// ADMIN PERFORMANCE APIs
// ===============================================

export const getAdminCandidateCommentsDetails = async (params = {}) => {
  const queryString = buildQueryString(params);
  const endpoint = queryString ? `/admin/performance/candidate-comments?${queryString}` : '/admin/performance/candidate-comments';
  return apiRequest(endpoint);
};

export const getAdminRecruiterFullDetails = async (params = {}) => {
  const queryString = buildQueryString(params);
  const endpoint = queryString ? `/admin/performance/recruiter-full-details?${queryString}` : '/admin/performance/recruiter-full-details';
  return apiRequest(endpoint);
};

export const getAdminTopPerformingRecruiters = async (params = {}) => {
  const queryString = buildQueryString(params);
  const endpoint = queryString ? `/admin/performance/top-performers?${queryString}` : '/admin/performance/top-performers';
  return apiRequest(endpoint);
};

// ===============================================
// ADMIN INCENTIVE APIs
// ===============================================

export const adminIncentiveAPI = {
  calculateIncentives: async (params = {}) => {
    const queryString = buildQueryString(params);
    const endpoint = queryString ? `/admin/incentives/calculate?${queryString}` : '/admin/incentives/calculate';
    return apiRequest(endpoint);
  },
  
  getRecruiterSummary: async (params = {}) => {
    const queryString = buildQueryString(params);
    const endpoint = queryString ? `/admin/incentives/summary/recruiter?${queryString}` : '/admin/incentives/summary/recruiter';
    return apiRequest(endpoint);
  },
  
  getTeamSummary: async (params = {}) => {
    const queryString = buildQueryString(params);
    const endpoint = queryString ? `/admin/incentives/summary/team?${queryString}` : '/admin/incentives/summary/team';
    return apiRequest(endpoint);
  },
  
  exportReport: async (params = {}) => {
    const queryString = buildQueryString(params);
    const endpoint = queryString ? `/admin/incentives/export?${queryString}` : '/admin/incentives/export';
    
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      credentials: 'include',
      mode: 'cors'
    });
    
    if (!response.ok) {
      throw new Error('Export failed');
    }
    
    return response.blob();
  },
  
  getRecruiterHistory: async (recruiterId, months = 6) => {
    const endpoint = `/admin/incentives/recruiter/${recruiterId}/history?months=${months}`;
    return apiRequest(endpoint);
  },
  
  updatePaymentStatus: async (data) => {
    return apiRequest('/admin/incentives/update-payment-status', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },
  
  markReviewsAsPaid: async (reviewIds, paymentDetails = {}) => {
    return apiRequest('/admin/incentives/mark-paid', {
      method: 'POST',
      body: JSON.stringify({
        review_ids: reviewIds,
        payment_date: paymentDetails.payment_date || new Date().toISOString(),
        payment_reference: paymentDetails.payment_reference || '',
        payment_notes: paymentDetails.payment_notes || 'Marked as paid by admin',
        payment_method: paymentDetails.payment_method || 'bank_transfer'
      })
    });
  },
  
  markReviewsAsUnpaid: async (reviewIds, reason = '') => {
    return apiRequest('/admin/incentives/mark-unpaid', {
      method: 'POST',
      body: JSON.stringify({
        review_ids: reviewIds,
        reason: reason || 'Marked as unpaid by admin'
      })
    });
  },
  
  processBatchPayment: async (data) => {
    return apiRequest('/admin/incentives/process-batch-payment', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },
  
  getPaymentStatistics: async (params = {}) => {
    const queryString = buildQueryString(params);
    const endpoint = queryString ? `/admin/incentives/payment-statistics?${queryString}` : '/admin/incentives/payment-statistics';
    return apiRequest(endpoint);
  },
  
  getPendingPayments: async (params = {}) => {
    const queryString = buildQueryString(params);
    const endpoint = queryString ? `/admin/incentives/pending-payments?${queryString}` : '/admin/incentives/pending-payments';
    return apiRequest(endpoint);
  },
  
  getPaymentAuditLog: async (params = {}) => {
    const queryString = buildQueryString(params);
    const endpoint = queryString ? `/admin/incentives/payment-audit-log?${queryString}` : '/admin/incentives/payment-audit-log';
    return apiRequest(endpoint);
  }
};

// ===============================================
// USER INCENTIVE APIs
// ===============================================

export const userIncentiveAPI = {
  getMyIncentives: async (params = {}) => {
    const queryString = buildQueryString(params);
    const endpoint = queryString ? `/incentives/my-incentives?${queryString}` : '/incentives/my-incentives';
    return apiRequest(endpoint);
  },
  
  getMyDashboard: async () => {
    return apiRequest('/incentives/my-dashboard');
  },
  
  getMyHistory: async (params = {}) => {
    const queryString = buildQueryString(params);
    const endpoint = queryString ? `/incentives/my-history?${queryString}` : '/incentives/my-history';
    return apiRequest(endpoint);
  },
  
  exportMyIncentives: async (params = {}) => {
    const queryString = buildQueryString(params);
    const endpoint = queryString ? `/incentives/my-incentives/export?${queryString}` : '/incentives/my-incentives/export';
    return apiRequest(endpoint);
  },
  
  getMyRanking: async (params = {}) => {
    const queryString = buildQueryString(params);
    const endpoint = queryString ? `/incentives/my-ranking?${queryString}` : '/incentives/my-ranking';
    return apiRequest(endpoint);
  },
  
  getTeamComparison: async (params = {}) => {
    const queryString = buildQueryString(params);
    const endpoint = queryString ? `/incentives/my-team-comparison?${queryString}` : '/incentives/my-team-comparison';
    return apiRequest(endpoint);
  }
};

// ===============================================
// ADMIN REMINDER ROUTES
// ===============================================

export const process24hrReminders = async () => {
  return apiRequest('/admin/reminders/24hr', { method: 'POST' });
};

export const processUnrequestedCandidatesReminders = async () => {
  return apiRequest('/admin/reminders/unrequested', { method: 'POST' });
};

// ===============================================
// ADDITIONAL ADMIN FUNCTIONS
// ===============================================

export const validateAdminToken = async () => {
  return apiRequest('/utils/validate-token');
};

export const adminLogout = () => {
  console.log('Logging out admin...');
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  window.location.href = '/login';
};

export const getCurrentAdminUser = () => {
  try {
    const user = getCurrentUser();
    return user;
  } catch (error) {
    console.error('Error getting admin user:', error);
    return {};
  }
};

export const adminHealthCheck = async () => {
  try {
    const response = await fetch(HEALTH_CHECK_URL, {
      mode: 'cors',
      credentials: 'include'
    });
    return await response.json();
  } catch (error) {
    console.error('Admin health check failed:', error);
    throw error;
  }
};

// ===============================================
// ADDITIONAL ADMIN DASHBOARD FUNCTIONS
// ===============================================

export const getAdminDashboardStats = async (params = {}) => {
  const queryString = buildQueryString(params);
  const endpoint = queryString ? `/admin/dashboard?${queryString}` : '/admin/dashboard';
  return apiRequest(endpoint);
};

export const getRecruitersPerformance = async (params = {}) => {
  const queryString = buildQueryString(params);
  const endpoint = queryString ? `/admin/dashboard/recruiters-performance?${queryString}` : '/admin/dashboard/recruiters-performance';
  return apiRequest(endpoint);
};

export const getReviewTrends = async (params = {}) => {
  const queryString = buildQueryString(params);
  const endpoint = queryString ? `/admin/dashboard/review-trends?${queryString}` : '/admin/dashboard/review-trends';
  return apiRequest(endpoint);
};

// ===============================================
// EXTENDED ADMIN INCENTIVE FUNCTIONS
// ===============================================

export const getRecruiterIncentiveHistory = async (recruiterId, params = {}) => {
  const queryString = buildQueryString(params);
  const endpoint = queryString 
    ? `/admin/incentives/recruiter/${recruiterId}/history?${queryString}` 
    : `/admin/incentives/recruiter/${recruiterId}/history`;
  return apiRequest(endpoint);
};

export const updateRecruiterPaymentStatus = async (recruiterId, paymentStatus, params = {}) => {
  return apiRequest(`/admin/incentives/recruiter/${recruiterId}/payment-status`, {
    method: 'POST',
    body: JSON.stringify({
      payment_status: paymentStatus,
      ...params
    })
  });
};

export const getRecruiterPaymentHistory = async (recruiterId, params = {}) => {
  const queryString = buildQueryString(params);
  const endpoint = queryString 
    ? `/admin/incentives/recruiter/${recruiterId}/payment-history?${queryString}` 
    : `/admin/incentives/recruiter/${recruiterId}/payment-history`;
  return apiRequest(endpoint);
};

export const generatePaymentSummary = async (params = {}) => {
  const queryString = buildQueryString(params);
  const endpoint = queryString 
    ? `/admin/incentives/payment-summary?${queryString}` 
    : '/admin/incentives/payment-summary';
  return apiRequest(endpoint);
};

export const exportPaymentReport = async (params = {}) => {
  const queryString = buildQueryString({
    ...params,
    include_payment_status: true,
    include_payment_details: true
  });
  const endpoint = queryString 
    ? `/admin/incentives/export-payment-report?${queryString}` 
    : '/admin/incentives/export-payment-report';
  
  const token = localStorage.getItem('token');
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`
    },
    credentials: 'include',
    mode: 'cors'
  });
  
  if (!response.ok) {
    throw new Error('Export failed');
  }
  
  return response.blob();
};

export const bulkApproveIncentives = async (data) => {
  return apiRequest('/admin/incentives/bulk-approve', {
    method: 'POST',
    body: JSON.stringify(data)
  });
};

export const generatePaymentReport = async (params = {}) => {
  const queryString = buildQueryString(params);
  const endpoint = queryString ? `/admin/incentives/payment-report?${queryString}` : '/admin/incentives/payment-report';
  return apiRequest(endpoint);
};

export const verifyPaymentStatus = async (reviewIds) => {
  return apiRequest('/admin/incentives/verify-payment-status', {
    method: 'POST',
    body: JSON.stringify({ review_ids: reviewIds })
  });
};

// ===============================================
// ERROR HANDLING
// ===============================================

export const handleApiError = (error) => {
  console.error('API Error:', error);
  
  if (error.message.includes('401') || error.message.includes('Unauthorized')) {
    console.log('Unauthorized - logging out');
    logout();
    return;
  }
  
  return error.message || 'An unexpected error occurred';
};

export const handleAdminApiError = (error) => {
  console.error('Admin API Error:', error);
  
  if (error.message.includes('401') || error.message.includes('Unauthorized')) {
    console.log('Admin unauthorized - logging out');
    adminLogout();
    return;
  }
  
  return error.message || 'An unexpected error occurred';
};

// ===============================================
// DEBUG UTILITIES
// ===============================================

export const testParameterCleaning = () => {
  const testParams = {
    status: '',
    recruiter_id: '',
    date_from: '',
    date_to: '',
    overdue_only: false,
    page: 1,
    limit: 20,
    search: '  ',
    valid_param: 'test'
  };
  
  console.log('Original params:', testParams);
  console.log('Cleaned params:', cleanParams(testParams));
  console.log('Query string:', buildQueryString(testParams));
  
  return {
    original: testParams,
    cleaned: cleanParams(testParams),
    queryString: buildQueryString(testParams)
  };
};

export const testDatabaseConnection = async () => {
  try {
    return await apiRequest('/admin/debug/test-connection');
  } catch (error) {
    console.error('Database test failed:', error);
    throw error;
  }
};

// Export parameter utilities for external use
export { cleanParams, buildQueryString };